import { type ForwardRefExoticComponent, Fragment, forwardRef } from 'react';

import { useDecoratedSlotContent } from '../../hooks/use-decorated-slot-content';
import { MediaObjectContent } from './media-object-content';
import { MediaObjectMedia } from './media-object-media';
import { MediaObjectRoot } from './media-object-root';
import type { MediaObjectPropsInterface } from './types/media-object.types';

/**
 * @since 7.6.0
 */
export const MediaObject = forwardRef<HTMLDivElement, MediaObjectPropsInterface>(
  (props: MediaObjectPropsInterface, forwardedRef) => {
    const { asChild = false, children, media, ...rest } = props;

    const content = useDecoratedSlotContent({
      children,
      asChild,
      template: (childContent) => (
        <Fragment>
          <MediaObject.Media>{media}</MediaObject.Media>
          <MediaObject.Content>{childContent}</MediaObject.Content>
        </Fragment>
      ),
      dependencies: [media],
    });

    return (
      <MediaObject.Root ref={forwardedRef} asChild={asChild} {...rest}>
        {content}
      </MediaObject.Root>
    );
  },
) as ForwardRefExoticComponent<MediaObjectPropsInterface> & {
  Root: typeof MediaObjectRoot;
  Media: typeof MediaObjectMedia;
  Content: typeof MediaObjectContent;
};

MediaObject.displayName = 'MediaObject';

MediaObject.Root = MediaObjectRoot;
MediaObject.Media = MediaObjectMedia;
MediaObject.Content = MediaObjectContent;
